/** SUNTRONIX ONE — tenant view of customer-owned stock held in Suntronix
 *  custody. Read-only pool with a Deploy action for eligible devices.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Warehouse, Rocket, Search, ShieldCheck } from "lucide-react";

const STATUS_STYLES = {
  AVAILABLE: "bg-emerald-50 border-emerald-200 text-emerald-700",
  REDEPLOYMENT_READY: "bg-sky-50 border-sky-200 text-sky-700",
  RESERVED: "bg-amber-50 border-amber-200 text-amber-700",
  DEPLOYED: "bg-slate-100 border-slate-300 text-slate-700",
  RECOVERY: "bg-orange-50 border-orange-200 text-orange-700",
  QUARANTINE: "bg-rose-50 border-rose-200 text-rose-700",
};
const DEPLOYABLE = new Set(["AVAILABLE", "REDEPLOYMENT_READY"]);
const CARDS = [
  ["AVAILABLE", "Available"], ["RESERVED", "Reserved"], ["DEPLOYED", "Deployed"],
  ["RECOVERY", "In recovery"], ["REDEPLOYMENT_READY", "Redeploy ready"],
];

export default function OurStockAtSuntronix() {
  const nav = useNavigate();
  const [summary, setSummary] = useState({});
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/customer-stock/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/customer-stock/list", { params: { status: status || undefined } })
      .then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const term = q.trim().toLowerCase();
  const rows = items.filter((r) => !term ||
    [r.product_name, r.serial, r.sku, r.brand, r.owning_entity_name]
      .some((f) => (f || "").toLowerCase().includes(term)));

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="our-stock-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-teal-600 grid place-items-center text-white"><Warehouse className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Our Stock at Suntronix</h1>
          <p className="text-[13px] text-slate-500 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-teal-600" /> Owned by you · held in Suntronix custody · deploy to your team anytime.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="our-stock-summary">
        {CARDS.map(([key, label]) => (
          <button key={key} onClick={() => setStatus(status === key ? "" : key)}
            data-testid={`our-stock-stat-${key}`}
            className={"text-left rounded-xl border p-3 transition-colors " +
              (status === key ? "border-teal-500 ring-2 ring-teal-100 bg-white" : "border-slate-200 bg-white hover:border-slate-300")}>
            <div className="text-[10px] font-medium text-slate-500">{label}</div>
            <div className="text-[22px] font-bold tabular-nums text-slate-900">{summary[key] ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, serial, SKU…"
            data-testid="our-stock-search" className="pl-8 h-9 text-[13px]" />
        </div>
        {status && (
          <Button variant="ghost" size="sm" onClick={() => setStatus("")} data-testid="our-stock-clear-filter">
            Clear filter ({status})
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="our-stock-table">
          <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            {["Product", "Serial", "Entity", "Warehouse", "Status", ""].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No stock in this view.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`our-stock-row-${r.id}`}>
                <td className="px-3 py-2 text-slate-800">{r.brand ? `${r.brand} ` : ""}{r.product_name}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.serial || `qty ${r.qty}`}</td>
                <td className="px-3 py-2 text-slate-500">{r.owning_entity_name || "—"}</td>
                <td className="px-3 py-2 text-slate-500">{r.warehouse}</td>
                <td className="px-3 py-2">
                  <span className={"text-[10px] font-semibold px-1.5 py-0.5 rounded border " + (STATUS_STYLES[r.status] || "bg-slate-100 border-slate-300 text-slate-600")}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  {DEPLOYABLE.has(r.status) && r.serial && (
                    <Button variant="ops" size="sm" className="h-7"
                      onClick={() => nav(`/deploy?serial=${encodeURIComponent(r.serial)}`)}
                      data-testid={`our-stock-deploy-${r.id}`}>
                      <Rocket className="h-3.5 w-3.5 mr-1" />Deploy
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
